import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ReportLayout } from '@/layouts/ReportLayout'
import { triggerReportPrint } from '@/lib/print'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'

const sampleExhibits = [
  {
    id: '8DJ3',
    exhibitor: 'Jordan Meadows',
    entry: 'Amber Honey (12 oz)',
    score: 96.4,
    notes: 'Exceptional clarity with balanced floral notes.',
  },
  {
    id: '0XP7',
    exhibitor: 'Priya Banerjee',
    entry: 'Creamed Honey',
    score: 93.1,
    notes: 'Smooth spreadability; mild crystallization at the rim.',
  },
  {
    id: 'FK2Z',
    exhibitor: 'Mateo Cruz',
    entry: 'Comb Honey Section',
    score: 91.8,
    notes: 'Clean cappings and uniform cells, slight moisture variance.',
  },
]

export default function ReportLayoutDemoPage() {
  const currentSlug = useShowSlug()
  const averageScore = useMemo(() => {
    if (sampleExhibits.length === 0) return 0
    const total = sampleExhibits.reduce((sum, exhibit) => sum + exhibit.score, 0)
    return Math.round((total / sampleExhibits.length) * 10) / 10
  }, [])

  return (
    <div className="space-y-6 print-surface">
      <div className="flex items-center justify-between print-hidden">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Report Layout Demo</h1>
          <p className="text-muted-foreground">
            Use this blueprint when constructing printable reports. Exhibit data references
            four-digit numeric Exhibit IDs.
          </p>
        </div>
        <Button onClick={triggerReportPrint}>Print Preview</Button>
      </div>

      <div className="rounded-lg border border-border bg-card shadow-sm print-shadowless">
        <ReportLayout
          header={
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                    Annual Honey Show Technical Council
                  </p>
                  <h2 className="text-2xl font-semibold tracking-tight">
                    Exhibit Performance Summary
                  </h2>
                </div>
                <Badge variant="secondary" className="text-sm font-medium">
                  Draft Layout
                </Badge>
              </div>
              <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                <div>
                  <span className="font-semibold text-foreground">Show:</span> Cascadia Invitational
                  2024
                </div>
                <div>
                  <span className="font-semibold text-foreground">Prepared for:</span> AHSTC Board
                  Review
                </div>
              </div>
            </div>
          }
        >
          <section className="space-y-3">
            <h3 className="text-xl font-semibold tracking-tight">Highlights</h3>
            <p className="text-sm leading-6 text-muted-foreground">
              The Cascadia Invitational showcased randomized Exhibit ID assignments and a balanced
              distribution of awards. Top exhibits maintained moisture content within AHSTC
              specifications and demonstrated consistent jar presentation.
            </p>
            <ul className="grid gap-2 text-sm sm:grid-cols-2">
              <li className="rounded-md border border-border bg-muted/40 p-3">
                <p className="text-sm font-medium text-foreground">Average adjudicated score</p>
                <p className="text-2xl font-semibold text-primary">{averageScore}</p>
              </li>
              <li className="rounded-md border border-border bg-muted/40 p-3">
                <p className="text-sm font-medium text-foreground">Exhibits at gold tier</p>
                <p className="text-2xl font-semibold text-primary">2 of 3</p>
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-xl font-semibold tracking-tight">Exhibit Details</h3>
            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-muted/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                      Exhibit ID
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                      Exhibitor
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                      Entry
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                      Score
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                      Judge Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sampleExhibits.map(exhibit => (
                    <tr key={exhibit.id} className="odd:bg-background even:bg-muted/30">
                      <td className="px-4 py-3 font-semibold tracking-wide text-foreground">
                        {exhibit.id}
                      </td>
                      <td className="px-4 py-3 text-foreground">{exhibit.exhibitor}</td>
                      <td className="px-4 py-3 text-foreground">{exhibit.entry}</td>
                      <td className="px-4 py-3 text-foreground">{exhibit.score.toFixed(1)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{exhibit.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-xl font-semibold tracking-tight">Next Steps</h3>
            <ol className="list-decimal space-y-2 pl-6 text-sm text-muted-foreground">
              <li>Finalize award certificates tied to each Exhibit ID for digital delivery.</li>
              <li>Share judge summaries with the AHSTC education committee.</li>
              <li>Schedule follow-up calibration for moisture testing equipment.</li>
            </ol>
          </section>
        </ReportLayout>
      </div>

      <div className="flex items-center justify-between rounded-md border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground print-hidden">
        <span>
          Need to integrate this layout elsewhere? Return to the{' '}
          <Link
            to={showAware('/reports', { currentSlug })}
            className="font-medium text-primary underline"
          >
            reports dashboard
          </Link>
          .
        </span>
      </div>
    </div>
  )
}
